import { Control, useWatch } from 'react-hook-form'
import { QuestionCard, CustomCheckboxInput, Accordion, ActionButton, SimpleText, Image, Label, Button, XStack } from '@my/ui'
import { InputField, AccordionTrigger, DropdownField } from '../helpers/Helper'
import images from '../../../assets/images'
import { April2026UpdatedOptions, confirmationOptions } from '../data'
import React from 'react'
import { FinancialAccountsTable, FinancialAccountsTableRows } from './LinkedFinancialAccounts'
import { color } from '@my/util'

interface AssetsProps {
  control: Control<any> // Control object from react-hook-form for managing form state
  linkedFinancialData: Record<string, any>
  goToAddAccountsTab: () => void;
}

const financialAccountOptions = (
  accounts: any[], 
  filterFn: (x: any, idx?: number) => boolean = () => true
) => accounts.filter(filterFn).map((acct) => ({ 
  label: `${acct.institutionName}: ${acct.name}`,
  value: `${acct.institution_id}|${acct.name}`
}));

export const Assets: React.FC<AssetsProps> = ({ control, linkedFinancialData, goToAddAccountsTab }) => {
  const emergencyFundExists = useWatch({ name: 'Assets - Emergency Fund - Exists'});
  const hasAddedAllMiscInvestmentAccounts = useWatch({ name: "Assets - Balance - Has Added Misc Asset Accounts"});
  const hasAddedAllRetirementAccounts = useWatch({ name: "Assets - Balance - Has Added Retirement Accounts"});
  const willAcquireAssets = useWatch({ name: "Assets - Acquire Assets"});
  const willSellAssets = useWatch({ name: "Assets - Sell Assets"});
  const ownOrRentFormValue = useWatch({ name: "Assets - Primary Residence - Has Rent or Mortgage"});
  const isRenter = ownOrRentFormValue === 'Rent';
  const isOwner = ['Own (with mortgage)', 'Own (no mortgage)'].includes(ownOrRentFormValue);

  const financialAccounts = FinancialAccountsTableRows(linkedFinancialData);
  const depositoryAccountOptions = financialAccountOptions(financialAccounts['depository'] || []);
  const propertyAccountOptions = financialAccountOptions(
    financialAccounts['misc'] || [],
    (acct) => acct.subtype === 'personal-property'
  );

  const missingAccountsButton = <Button onPress={() => goToAddAccountsTab()}>Add Missing Accounts</Button>;
  const missingPropertyButton = <Button onPress={() => goToAddAccountsTab()}>Add Missing Property/Real-estate</Button>;
  const missingAccountsMessage = <XStack alignItems='center' gap={5}>
    <SimpleText>For the most accurate analysis, make sure we have your complete Financial Profile:</SimpleText>
    {missingAccountsButton}
  </XStack>
  const missingPropertyMessage = <XStack alignItems='center' gap={5}>
    <SimpleText>If your primary residence is not listed above, add it as a real-estate asset:</SimpleText>
    {missingPropertyButton}
  </XStack>
  return (
    <>
      {/* Question Card for Savings Accounts */}
      <QuestionCard questionGroupTitle="Please review the balances of all your depository and investment accounts:" /* "How much do you currently have in savings? Please specify the types of accounts (e.g., checking, savings, high-yield - this does not include investment accounts)" */>
        <Accordion overflow="hidden" type="multiple" defaultValue={['a1']}>
          <Accordion.Item value="a1">
            <AccordionTrigger title={'Depository (Checking & Savings)'} />
            <Accordion.HeightAnimator transition="medium">
              <Accordion.Content transition="medium" exitStyle={{ opacity: 0 }} style={{backgroundColor: 'white'}} paddingHorizontal={0}>
                <FinancialAccountsTable linkedFinancialData={linkedFinancialData} type={'depository'} />
              </Accordion.Content>
            </Accordion.HeightAnimator>
          </Accordion.Item>
          <Accordion.Item value="a2">
            <AccordionTrigger title={'Investments & Other Assets'} />
            <Accordion.HeightAnimator transition="medium">
              <Accordion.Content transition="medium" exitStyle={{ opacity: 0 }} style={{backgroundColor: 'white'}} paddingHorizontal={0}>
                <FinancialAccountsTable linkedFinancialData={linkedFinancialData} type={['investment', 'other', 'misc' as any]} />
              </Accordion.Content>
            </Accordion.HeightAnimator>
          </Accordion.Item>
        </Accordion>
        <ActionButton
          unstyled
          onPress={() => goToAddAccountsTab()}
          //disabled={!ready}
          borderWidth={1}
          borderColor={color.tzaGreen}
          hoverStyle={{backgroundColor: '#e7e7e7'}}
          borderStyle="dashed"
          height={90}
          bg={color.white}
          style={{ alignItems: 'center', justifyComment: 'center', paddingTop:10 }}
          borderRadius={10}
          gap={5}
          icon={
            <Image
              src={images.linkAccount}
              style={{ height: 35, width: 45 }}
              resizeMode="contain"
            />
          }
        >
          <SimpleText fontSize={14} fontWeight={'700'} color={color.grey}>
            Add any missing Assets
          </SimpleText>
        </ActionButton>
      </QuestionCard>

      <QuestionCard questionGroupTitle="Do you have a plan for financial emergencies?">
        <CustomCheckboxInput
          options={confirmationOptions}
          control={control}
          name="Financial Security - Financial Emergencies"
        />
      </QuestionCard>
      {/* Question Card for Emergency Fund */}
      <QuestionCard questionGroupTitle="Do you have a specific emergency fund? How much is in it?">
        <CustomCheckboxInput
          options={confirmationOptions}
          control={control}
          name="Assets - Emergency Fund - Exists" // Checkbox to indicate if an emergency fund exists
          //rules={{required: true}}
        />
        {emergencyFundExists && <>
          <Label htmlFor="Assets - Emergency Fund - Accounts">Please select the account(s) containing your emergency fund. (If your emergency fund is spread accross multiple accounts, please select all of them.)</Label>
          {depositoryAccountOptions.length ? <CustomCheckboxInput
            options={depositoryAccountOptions}
            control={control}
            name="Assets - Emergency Fund - Accounts"
            multiple
          /> : missingAccountsButton}
        </>}
      </QuestionCard>
      {emergencyFundExists && <QuestionCard questionGroupTitle="Does your emergency fund have 3-6 months worth of savings in it? (To cover all Need expenses?)">
        <CustomCheckboxInput
          options={confirmationOptions}
          control={control}
          name="Assets - Emergency Fund - Has 3-6 Months Savings" // Checkbox to indicate if an emergency fund exists
        />
      </QuestionCard>}
      <QuestionCard questionGroupTitle="Do you currently own or rent your primary residence?">
        <DropdownField
          control={control}
          name="Assets - Primary Residence - Has Rent or Mortgage"
          items={April2026UpdatedOptions.Assets.homeOwnershipStatus}
          native
        />
        {isOwner && <>
          <Label htmlFor="Assets - Emergency Fund - Accounts">Please select the property where you primarily reside.</Label>
          {propertyAccountOptions.length ? <><CustomCheckboxInput
            options={propertyAccountOptions}
            control={control}
            name="Assets - Primary Residence - Account Market Value"
            //multiple
          />{missingPropertyMessage}</> : missingPropertyButton}
        </>}
        {isRenter && <InputField
          control={control}
          name='Expenses - Monthly - Rent'
          placeholder="What is your monthly rent?"
          icon={images.money}
        />}
      </QuestionCard>


      {/* Question Card for Miscellaneous Assets */}
      <QuestionCard questionGroupTitle="Have you added all your investments or other miscellaneous assets (e.g., stocks, bonds, real estate, valuable personal property, or business interests)?">
        <CustomCheckboxInput
          options={confirmationOptions}
          control={control}
          name="Assets - Balance - Has Added Misc Asset Accounts" // Checkbox for miscellaneous investment accounts
        />
        {hasAddedAllMiscInvestmentAccounts === false ? missingAccountsMessage : null}
      </QuestionCard>

      {/* Question Card for Future Asset Plans */}
      <QuestionCard questionGroupTitle="Do you have any plans to acquire significant assets in the near future?">
        <CustomCheckboxInput
          options={confirmationOptions}
          control={control}
          name="Assets - Acquire Assets" // Checkbox for future acquisition/sale plans
        />
        {willAcquireAssets && <InputField
          control={control}
          name='Assets - Acquire Assets - How much?'
          placeholder="What is the value of the asset(s) you plan to acquire?"
          icon={images.money}
        />}
        {willAcquireAssets && <InputField
          control={control}
          name='Assets - Acquire Assets - Funding Source?'
          placeholder="How do you plan to fund this acquisition? (Where will the funds come from?)"
          //icon={images.money}
        />}
        {willAcquireAssets && <>
          <Label>Will this acquisition be made using cash or debt?</Label>
          <CustomCheckboxInput
            options={[
              {label: 'Debt', value: 'debt'},
              {label: 'Cash', value: 'cash'}
            ]}
            control={control}
            name='Assets - Acquire Assets - Funding Type' // Checkbox for future acquisition/sale plans
          />
        </>}
      </QuestionCard>
      <QuestionCard questionGroupTitle="Do you have any plans to sell significant assets in the near future?">
        <CustomCheckboxInput
          options={confirmationOptions}
          control={control}
          name="Assets - Sell Assets" // Checkbox for future acquisition/sale plans
        />
        {willSellAssets && <InputField
          control={control}
          name='Assets - Sell Assets - How much?'
          placeholder="What is the value of the asset(s) you plan to sell?"
          icon={images.money}
        />}
        {willSellAssets && <InputField
          control={control}
          name='Assets - Sell Assets - Where to put Proceeds?'
          placeholder="Where will you put the proceeds from what you plan to sell?"
          //icon={images.money}
        />}
      </QuestionCard>

      {/* Question Card for Retirement Savings Strategies */}
      {/* <QuestionCard questionGroupTitle="What are your retirement savings strategies? Do you have any pension plans, IRAs, or other retirement accounts?">
        <InputField
          control={control}
          name="Assets - Balance - Other Retirement Accounts (excluding 401k/IRA)"
          placeholder="Total Balance of all other (non-401K/IRA) Retirement accounts"
          icon={images.money}
        />
      </QuestionCard> */}
      <QuestionCard questionGroupTitle="Have you added all your retirement savings accounts? (e.g., pension plans, IRAs, or other retirement accounts)?">
        <CustomCheckboxInput
          options={confirmationOptions}
          control={control}
          name="Assets - Balance - Has Added Retirement Accounts" // Checkbox for miscellaneous investment accounts
        />
        {hasAddedAllRetirementAccounts === false ? missingAccountsMessage : null}
      </QuestionCard>
    </>
  )
}
